import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faChartLine, 
  faTrophy, 
  faGamepad,
  faEdit,
  faShare,
  faUsers,
  faCog,
  faCalendar,
  faStar
} from '@fortawesome/free-solid-svg-icons';

const Profile = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'games', label: 'Recent Games' },
    { id: 'stats', label: 'Statistics' },
    { id: 'achievements', label: 'Achievements' },
  ];

  const mockProfile = {
    username: 'ChessEnthusiast2024',
    realName: 'Alex Chen',
    rating: 1847,
    country: 'United States',
    memberSince: 'January 2022',
    gamesPlayed: 1234,
    winRate: 68,
    avatar: '♟️'
  };

  const recentGames = [
    { opponent: 'GrandmasterAlpha', result: 'Win', rating: '+12', time: '10+0', date: '2 hours ago' },
    { opponent: 'TacticalTina', result: 'Loss', rating: '-8', time: '5+3', date: '1 day ago' },
    { opponent: 'EndgameWizard', result: 'Draw', rating: '+2', time: '15+10', date: '2 days ago' },
    { opponent: 'BlitzBeast', result: 'Win', rating: '+15', time: '3+2', date: '3 days ago' },
    { opponent: 'OpeningExplorer', result: 'Loss', rating: '-10', time: '10+0', date: '4 days ago' },
  ];

  const achievements = [
    { title: 'First Win', description: 'Won your first game', icon: faTrophy, earned: true },
    { title: 'Puzzle Master', description: 'Solved 100 puzzles', icon: faStar, earned: true },
    { title: 'Speed Demon', description: 'Won 10 bullet games', icon: faGamepad, earned: true },
    { title: 'Endgame Expert', description: 'Won from a losing position', icon: faChartLine, earned: false },
    { title: 'Social Player', description: 'Made 50 friends', icon: faUsers, earned: false },
    { title: 'Tournament Player', description: 'Participated in 5 tournaments', icon: faTrophy, earned: false },
  ];

  const stats = {
    totalGames: 1234,
    wins: 839,
    losses: 272,
    draws: 123,
    winStreak: 7,
    bestWinStreak: 23,
    averageGameLength: '18m 34s',
    favoriteOpening: 'Sicilian Defense'
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="max-w-6xl mx-auto px-4"
    >
      {/* Profile Header */}
      <div className="bg-dark-secondary border border-dark-tertiary rounded-lg p-8 mb-8">
        <div className="flex flex-col md:flex-row items-center md:items-start space-y-4 md:space-y-0 md:space-x-6">
          {/* Avatar */}
          <div className="w-24 h-24 bg-dark-tertiary rounded-full flex items-center justify-center text-4xl">
            {mockProfile.avatar}
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex flex-col md:flex-row md:items-center md:space-x-4 mb-2">
              <h1 className="text-2xl font-bold text-light-primary">{mockProfile.username}</h1>
              <div className="flex items-center justify-center md:justify-start space-x-2">
                <FontAwesomeIcon icon={faStar} className="text-yellow-400" />
                <span className="text-xl font-semibold text-light-primary">{mockProfile.rating}</span>
              </div>
            </div>
            <p className="text-light-secondary mb-1">{mockProfile.realName}</p>
            <p className="text-light-secondary text-sm">
              {mockProfile.country} • Member since {mockProfile.memberSince}
            </p>
            <div className="flex justify-center md:justify-start space-x-6 mt-4">
              <div className="text-center">
                <div className="text-light-primary font-semibold">{mockProfile.gamesPlayed}</div>
                <div className="text-light-secondary text-sm">Games</div>
              </div>
              <div className="text-center">
                <div className="text-light-primary font-semibold">{mockProfile.winRate}%</div>
                <div className="text-light-secondary text-sm">Win Rate</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2">
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <FontAwesomeIcon icon={faEdit} className="mr-2" />
              Edit Profile
            </button>
            <button className="bg-dark-tertiary text-light-primary px-4 py-2 rounded-lg hover:bg-dark-tertiary/80 transition-colors">
              <FontAwesomeIcon icon={faShare} />
            </button>
            <button className="bg-dark-tertiary text-light-primary px-4 py-2 rounded-lg hover:bg-dark-tertiary/80 transition-colors">
              <FontAwesomeIcon icon={faCog} />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-8 bg-dark-secondary rounded-lg p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 px-4 rounded-md transition-all ${
              activeTab === tab.id
                ? 'bg-dark-primary text-light-primary'
                : 'text-light-secondary hover:text-light-primary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Rating Chart Placeholder */}
            <div className="bg-dark-secondary border border-dark-tertiary rounded-lg p-6">
              <div className="flex items-center space-x-2 mb-4">
                <FontAwesomeIcon icon={faChartLine} className="text-green-400" />
                <h2 className="text-xl font-bold text-light-primary">Rating Progress</h2>
              </div>
              <div className="h-48 bg-dark-primary rounded-lg flex items-center justify-center">
                <p className="text-light-secondary">Rating chart visualization coming soon</p>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-light-primary font-semibold">1847</div>
                  <div className="text-light-secondary text-sm">Current</div>
                </div>
                <div>
                  <div className="text-green-400 font-semibold">1923</div>
                  <div className="text-light-secondary text-sm">Peak</div>
                </div>
                <div>
                  <div className="text-blue-400 font-semibold">+76</div>
                  <div className="text-light-secondary text-sm">This Month</div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-dark-secondary border border-dark-tertiary rounded-lg p-6">
              <h2 className="text-xl font-bold text-light-primary mb-4">Quick Stats</h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-light-secondary">Current Win Streak</span>
                  <span className="text-light-primary font-semibold">{stats.winStreak} games</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-secondary">Best Win Streak</span>
                  <span className="text-light-primary font-semibold">{stats.bestWinStreak} games</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-secondary">Average Game Length</span>
                  <span className="text-light-primary font-semibold">{stats.averageGameLength}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-secondary">Favorite Opening</span>
                  <span className="text-light-primary font-semibold">{stats.favoriteOpening}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-light-secondary">Games This Week</span>
                  <span className="text-light-primary font-semibold">23 games</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'games' && (
          <div className="bg-dark-secondary border border-dark-tertiary rounded-lg p-6">
            <h2 className="text-xl font-bold text-light-primary mb-4">Recent Games</h2>
            <div className="space-y-3">
              {recentGames.map((game, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="flex items-center justify-between p-4 bg-dark-primary rounded-lg hover:bg-dark-tertiary/50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-dark-tertiary rounded-full flex items-center justify-center">
                      <FontAwesomeIcon icon={faUser} className="text-light-secondary" />
                    </div>
                    <div>
                      <p className="text-light-primary font-medium">{game.opponent}</p>
                      <p className="text-light-secondary text-sm">{game.time} • {game.date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-semibold ${
                      game.result === 'Win' ? 'text-green-400' :
                      game.result === 'Loss' ? 'text-red-400' :
                      'text-yellow-400'
                    }`}>
                      {game.result}
                    </p>
                    <p className="text-light-secondary text-sm">{game.rating}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <button className="w-full mt-4 text-light-secondary hover:text-light-primary transition-colors">
              Load More Games
            </button>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-dark-secondary border border-dark-tertiary rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-light-primary mb-2">{stats.totalGames}</div>
              <div className="text-light-secondary">Total Games</div>
            </div>
            <div className="bg-dark-secondary border border-dark-tertiary rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-green-400 mb-2">{stats.wins}</div>
              <div className="text-light-secondary">Wins</div>
            </div>
            <div className="bg-dark-secondary border border-dark-tertiary rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-red-400 mb-2">{stats.losses}</div>
              <div className="text-light-secondary">Losses</div>
            </div>
            <div className="bg-dark-secondary border border-dark-tertiary rounded-lg p-6 text-center">
              <div className="text-3xl font-bold text-yellow-400 mb-2">{stats.draws}</div>
              <div className="text-light-secondary">Draws</div>
            </div>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className={`bg-dark-secondary border rounded-lg p-6 ${
                  achievement.earned 
                    ? 'border-yellow-400' 
                    : 'border-dark-tertiary'
                }`}
              >
                <div className="flex items-center space-x-3 mb-3">
                  <FontAwesomeIcon 
                    icon={achievement.icon} 
                    className={`text-2xl ${
                      achievement.earned ? 'text-yellow-400' : 'text-light-secondary'
                    }`}
                  />
                  <div>
                    <h3 className={`font-semibold ${
                      achievement.earned ? 'text-light-primary' : 'text-light-secondary'
                    }`}>
                      {achievement.title}
                    </h3>
                    <p className="text-light-secondary text-sm">{achievement.description}</p>
                  </div>
                </div>
                {achievement.earned && (
                  <div className="flex items-center space-x-2 text-sm">
                    <FontAwesomeIcon icon={faCalendar} className="text-light-secondary" />
                    <span className="text-light-secondary">Earned recently</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Profile;